import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { SOCKET_TOURNAMENT_UPDATE } from '../../config/constants';
import { analytics } from '../../services/analytics';
import type { EventAction, WidgetEventOptionalArgs } from '../../services/analytics/types';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import { devError } from '../../utils/log';
import { type CoinType } from '../cointype/CoinTypeStore';
import type {
  FeatureFetchStatus,
  FeatureFetchType,
  FeatureManagerDialogType,
  IFeatureManagerStore,
} from '../feature-manager/types';
import type { GameLabel } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import type {
  Tournament,
  TournamentEventParams,
  TournamentEventShownBy,
  TournamentResponse,
  TournamentV2Response,
} from './types';

export class TournamentStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    this.tournamentsFlag = resolveFlag('tournaments', true);
    this.enablePreviousTournamentFlag = resolveFlag('enable_previous_tournament_logic', true);

    ldClient?.on('ready', () => {
      this.setTournamentsFlag(ldClient?.variation('tournaments', true));
      this.enablePreviousTournamentFlag = ldClient?.variation('enable_previous_tournament_logic', true);
    });

    ldClient?.on('change', (changes) => {
      if ('tournaments' in changes) {
        this.updateTournamentsFlag(changes.tournaments.current);
      }
    });

    if (this.tournamentsFlag) {
      socket.on(SOCKET_TOURNAMENT_UPDATE, this.subscribeToSocket.bind(this));
    }
  }

  @observable
  public tournamentsFlag: boolean = false;

  @observable
  public enablePreviousTournamentFlag: boolean = false;

  @observable
  public currentTournament: Tournament | null = null;

  @observable
  public loading: Record<string, Record<string, boolean>> = {
    GC: {},
    SC: {},
  };

  @observable
  public tournamentGameLabel: GameLabel | null = null;

  @computed
  public get isTournamentDialogOpen() {
    return this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenTournamentDialog;
  }

  @computed
  public get hasLeaderboard() {
    const { coinType } = this.rootStore.coinTypeStore;
    return !!this.currentTournament?.leaderboards[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @action
  private setTournamentsFlag(value: boolean) {
    this.tournamentsFlag = value;
  }

  @action
  public setLoading(key: string, loading: boolean, coinType?: CoinType) {
    const coinTypeValue = coinType || this.rootStore.coinTypeStore.coinType;
    this.loading = {
      ...this.loading,
      [coinTypeValue]: {
        ...this.loading[coinTypeValue],
        [key]: loading,
      },
    };
  }

  @action
  public getLoading(key: string) {
    const { coinType } = this.rootStore.coinTypeStore;
    return this.loading[coinType][key];
  }

  @action
  private setCurrentTournament(tournament: Tournament | null) {
    this.currentTournament = tournament;
  }

  @action
  public setTournamentGameLabel(gameLabel: GameLabel | null) {
    this.tournamentGameLabel = gameLabel;
  }

  @action clearPlayerCurrentTournament() {
    this.setCurrentTournament(null);
    this.closeTournamentDialog();
  }

  @action
  public closeTournamentDialog() {
    if (this.isTournamentDialogOpen) {
      this.rootStore.uiStore.setCloseDialog();
      if (this.currentTournament) {
        if (this.currentTournament.status === 'finished') {
          this.rootStore.featureManagerStore.onDialogFinished('PostTournament');
        } else {
          this.rootStore.featureManagerStore.onDialogFinished('Tournament');
        }
      }
    }
  }

  public updateTournamentsFlag(value: boolean) {
    if (this.tournamentsFlag === value) return;

    this.setTournamentsFlag(value);

    const subscription = this.subscribeToSocket.bind(this);

    if (this.tournamentsFlag) {
      socket.on(SOCKET_TOURNAMENT_UPDATE, subscription);
    } else {
      socket.off(SOCKET_TOURNAMENT_UPDATE, subscription);
    }
  }

  private updateTournamentByType(data: Tournament | null, coinType: 'gc' | 'sc') {
    if (!data) {
      this.setCurrentTournament(null);
      return;
    }
    if (!this.currentTournament) {
      this.setCurrentTournament(data);
      return;
    }
    this.setCurrentTournament({
      ...data,
      leaderboards: {
        ...this.currentTournament.leaderboards,
        [coinType]: data.leaderboards?.[coinType],
      },
    });
  }

  public async startTournamentForPlayer() {
    try {
      if (!this.currentTournament) {
        devError('No current tournament');
        return;
      }

      this.setLoading('startTournamentForPlayer', true);
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/start/${this.currentTournament.key}/${coinType}`);
      await this.fetch();
    } finally {
      this.setLoading('startTournamentForPlayer', false);
    }
  }

  public async finishTournamentForPlayer() {
    try {
      if (!this.currentTournament) {
        devError('No current tournament');
        return;
      }

      this.setLoading('finishTournamentForPlayer', true);
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/finish/${this.currentTournament?.key}/${coinType}`);
    } finally {
      this.setLoading('finishTournamentForPlayer', false);
    }
  }

  public handleAnalytics({
    event_action = 'click',
    params,
  }: {
    event_action: EventAction;
    params?: TournamentEventParams & WidgetEventOptionalArgs;
  }) {
    try {
      const event = {
        tournament_id: this.currentTournament?.key,
        game_id: this.currentTournament?.tournamentGroup.gameId,
        tournament_status: this.currentTournament?.status,
        tournament_type: this.currentTournament?.tournamentGroup?.type,
        ...this.setPrizeForAnalytics(),
        ...params,
      };
      const { Tournament, PostTournament } = this.rootStore.featureManagerStore.dialogManagerData || {};
      const isPostTournament = this.currentTournament?.status === 'finished';
      const feature_order = (isPostTournament ? PostTournament?.order : Tournament?.order) || 0;
      const feature_sub_type = isPostTournament ? 'post_tournament' : 'tournament';

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      void analytics.sendEvent('widget', {
        event_action,
        feature_order,
        feature_type: 'tournament',
        feature_sub_type,
        ...event,
      });
    } catch (e) {
      devError('handleAnalytics', e);
    }
  }

  private setPrizeForAnalytics() {
    if (!this.currentTournament) {
      devError('No current tournament');
      return;
    }

    const coin_type = this.rootStore.coinTypeStore.coinType === 'GC' ? 'cc' : 'sc';
    if (coin_type === 'cc') {
      const gcPlayerPosition = this.currentTournament.leaderboards.gc.player.position;
      const gcPlayerPrize = this.currentTournament.leaderboards.gc.player.prize;
      const gcPrizePlain = gcPlayerPrize ? toJS(gcPlayerPrize) : null;
      const score = this.currentTournament.leaderboards.gc.player.score;

      return {
        position: !isNil(gcPlayerPosition) ? gcPlayerPosition + 1 : null,
        prize: gcPrizePlain,
        coin_type,
        score,
      };
    }

    const scPlayerPosition = this.currentTournament.leaderboards.sc.player.position;
    const scPlayerPrize = this.currentTournament.leaderboards.sc.player.prize;
    const scPrizePlain = scPlayerPrize ? toJS(scPlayerPrize) : null;
    const score = this.currentTournament.leaderboards.sc.player.score;

    return {
      position: !isNil(scPlayerPosition) ? scPlayerPosition + 1 : null,
      prize: scPrizePlain,
      coin_type,
      score,
    };
  }

  private openDialog(triggeredBy: TournamentEventShownBy) {
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: {
        shown_by: triggeredBy,
      },
    });
  }

  private isFirstTimeOpen: Record<CoinType, boolean> = {
    GC: false,
    SC: false,
  };

  private setFirstTimeOpen(coinType: CoinType) {
    this.isFirstTimeOpen[coinType] = true;
  }

  public dialogTypes: FeatureManagerDialogType[] = ['Tournament', 'PostTournament'];

  public startDialogs(dialogType?: FeatureManagerDialogType) {
    const coinType = this.rootStore.coinTypeStore.coinType;
    const isFirstTimeOpen = this.isFirstTimeOpen[coinType];
    if (this.currentTournament && this.hasLeaderboard) {
      if (dialogType === 'Tournament') {
        if (coinType === 'GC') {
          return false;
        }

        switch (this.currentTournament.status) {
          case 'upcoming':
            // don't auto open for GC upcoming tournaments, open once for SC
            if (!isFirstTimeOpen) {
              this.openDialog('log_in');
              this.setFirstTimeOpen(coinType);
              return true;
            }
            return false;
          case 'ongoing':
            if (this.currentTournament.playerStatus === 'pending') {
              this.openDialog('log_in');
              return true;
            }
            return false;
          case 'finished':
            return false;
          default:
            return false;
        }
      } else if (dialogType === 'PostTournament') {
        const isTournamentFinishedForPlayer =
          this.currentTournament.status === 'finished' && this.currentTournament.playerStatus === 'started';
        if (isTournamentFinishedForPlayer) {
          this.openDialog('log_in');
          return true;
        }
      }
    }

    return false;
  }

  public async fetchTournamentV2(coinType: 'gc' | 'sc') {
    try {
      const res = await getRequest<{ data: TournamentV2Response }>(`/v2/tournament/current/${coinType}`);
      return res.data.tournaments;
    } catch (error) {
      devError('FetchTournamentV2Error', JSON.stringify({ error }));
      return {
        currentTournament: null,
        previousTournament: null,
      };
    }
  }

  public async fetchTournament(coinType: 'gc' | 'sc') {
    try {
      if (this.enablePreviousTournamentFlag) {
        const res = await this.fetchTournamentV2(coinType);
        return res;
      }

      const res = await getRequest<{ data: TournamentResponse }>(`tournament/current/${coinType}`);

      return {
        currentTournament: res.data.tournament,
        previousTournament: null,
      };
    } catch (error) {
      devError('FetchTournamentError', JSON.stringify({ error }));
      return {
        currentTournament: null,
        previousTournament: null,
      };
    }
  }

  public async fetch(featureManagerOptions?: { fetchType: FeatureFetchType }): Promise<FeatureFetchStatus> {
    const { coinType } = this.rootStore.coinTypeStore;

    try {
      if (!this.tournamentsFlag) return 'OFF';

      const isLoading = this.getLoading('fetchPlayerCurrentTournament');

      if (isLoading) return 'FETCHING';
      this.setLoading('fetchPlayerCurrentTournament', true, coinType);
      const lowercasedCoinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      const res = await this.fetchTournament(lowercasedCoinType);
      const { currentTournament, previousTournament } = res;

      let tournament;

      if (previousTournament && previousTournament?.leaderboards[lowercasedCoinType]) {
        tournament = previousTournament;
      } else if (currentTournament && currentTournament?.leaderboards[lowercasedCoinType]) {
        tournament = currentTournament;
      } else {
        this.clearPlayerCurrentTournament();
      }

      if (tournament) {
        const hasStarted = dayjs().isAfter(dayjs(tournament.tournamentGroup.startDate));

        if (hasStarted && tournament.status === 'upcoming') {
          this.clearPlayerCurrentTournament();
        } else {
          this.updateTournamentByType(tournament, lowercasedCoinType);
        }

        const isPlayerPending = tournament.playerStatus === 'pending';
        const isTournamentFinishedForPlayer = tournament.status === 'finished' && tournament.playerStatus === 'started';

        if (isPlayerPending || isTournamentFinishedForPlayer) {
          const dialogTypes = [
            isTournamentFinishedForPlayer ? 'PostTournament' : 'Tournament',
          ] as FeatureManagerDialogType[];

          // If not coming from feature manager, open dialog immediately
          if (!featureManagerOptions && !this.isTournamentDialogOpen) {
            this.rootStore.featureManagerStore.addDialogsToSequenceQueue(dialogTypes);
          }
        }
        if (tournament.gameLabel) {
          this.setTournamentGameLabel(tournament.gameLabel);
        }

        this.setLoading('fetchPlayerCurrentTournament', false, coinType);
        return 'READY';
      }

      this.setLoading('fetchPlayerCurrentTournament', false, coinType);
      return 'OFF';
    } catch (error) {
      devError('FetchTournamentError', JSON.stringify({ error }));
      this.setLoading('fetchPlayerCurrentTournament', false, coinType);
      return 'ERROR';
    }
  }

  private subscribeToSocket(_socketData: any) {}
}
